/**
 * SUBSCRIPTIONS/INGRESS do marketdata (bloco 3 — assinaturas) — extraído de
 * marketdata.jsx na modularização dos god-objects: taps raw do seam
 * (register/dispatch com guard I2), ref-counting de quotes via
 * QuoteSubscriptionManager, transporte BookByPrice (register/unregister no
 * hub + broadcast raw + re-register pós-reconexão) e reset do cache diário do
 * quote worker. Corpos byte-a-byte, `this` → `md`; estado NA INSTÂNCIA.
 * Contrato pinado ANTES da extração: marketdataIngressHistoryContract.test.js
 * (+ quote-ref-counting.spec no e2e).
 */
import logger from "../../utils/logger";

export const registerRawTap = (md, symbol, cb) => {
  const entry = { symbol, cb };
  md.rawQuoteTaps.push(entry);
  return () => {
    const i = md.rawQuoteTaps.indexOf(entry);
    if (i !== -1) md.rawQuoteTaps.splice(i, 1);
  };
};

/**
 * Fan a raw quote out to every matching seam-reconcile tap. A tap callback
 * must never throw the hot path; guard each call.
 */
export const dispatchRawQuoteToTaps = (md, quote) => {
  const s = quote.S;
  for (let i = 0; i < md.rawQuoteTaps.length; i++) {
    const t = md.rawQuoteTaps[i];
    if (t.symbol === s || md.isSameFamily(t.symbol, s)) {
      try {
        t.cb(quote);
      } catch (e) {
        console.error("[Marketdata] raw tap callback error:", e);
      }
    }
  }
};

/**
 * Register a symbol for real-time quotes with subscriber-based reference counting.
 * Delegates to QuoteSubscriptionManager — SignalR calls happen via callbacks.
 */
export const registerQuote = (md, symbol, subscriberId = "legacy") => {
  md.subscriptionManager.register(symbol, subscriberId);
};

/**
 * Unregister a symbol from real-time quotes.
 * Only unregisters from SignalR when the last subscriber unregisters.
 */
export const unregisterQuote = (md, symbol, subscriberId = "legacy") => {
  md.subscriptionManager.unregister(symbol, subscriberId);
};

// ============================================
// BookByPrice (BBP) Transport
// ============================================
//
// Subscriber convention: `book-${panelId}` (mirrors Chart's `chart-${id}`).
// First subscriber for a symbol issues Register("bbp", symbol) upstream;
// last unsubscriber issues Unregister("bbp", symbol). Server fans `B`
// events to the connection; this layer forwards them raw via
// receiver.OnBookByPriceRaw(book). No model/cache/UI in Phase 2.

/**
 * Register a panel/consumer to receive real-time BookByPrice for `symbol`.
 * @param {string} symbol - non-empty symbol (e.g., "PETR4", "WINFUT")
 * @param {string} subscriberId - non-empty, e.g., `book-${panelId}`
 */
export const registerBookByPrice = (md, symbol, subscriberId) => {
  md.bookByPriceSubscriptionManager.register(symbol, subscriberId);
};

/**
 * Unregister a previously registered BookByPrice consumer.
 * Only issues upstream Unregister when the last subscriber leaves.
 */
export const unregisterBookByPrice = (md, symbol, subscriberId) => {
  md.bookByPriceSubscriptionManager.unregister(symbol, subscriberId);
};

export const hasBookByPriceSubscribers = (md, symbol) => {
  return md.bookByPriceSubscriptionManager.hasSubscribers(symbol);
};

export const debugBookByPriceSubscriptions = (md) => {
  return md.bookByPriceSubscriptionManager.debug();
};

export const sendBookByPriceRegister = (md, symbol) => {
  if (
    !md.signalRConnection ||
    typeof md.signalRConnection.invoke !== "function"
  ) {
    return;
  }
  md.signalRConnection
    .invoke("register", "bbp", symbol)
    .then(() => {
      console.log(
        `[Marketdata] 📡 BBP ${symbol} registered (first subscriber)`,
      );
      logger.diagnostic.info(`BBP registered: ${symbol}`, {
        symbol,
        event: "bbp-register",
      });
    })
    .catch((err) => {
      console.error(`[Marketdata] ❌ BBP register failed for ${symbol}:`, err);
      logger.diagnostic.error(`BBP register failed: ${symbol}`, {
        symbol,
        error: String(err),
      });
    });
};

export const sendBookByPriceUnregister = (md, symbol) => {
  if (
    !md.signalRConnection ||
    typeof md.signalRConnection.invoke !== "function"
  ) {
    return;
  }
  md.signalRConnection
    .invoke("unregister", "bbp", symbol)
    .then(() => {
      console.log(
        `[Marketdata] 🔌 BBP ${symbol} unregistered (last subscriber)`,
      );
      logger.diagnostic.info(`BBP unregistered: ${symbol}`, {
        symbol,
        event: "bbp-unregister",
      });
    })
    .catch((err) => {
      console.error(
        `[Marketdata] ❌ BBP unregister failed for ${symbol}:`,
        err,
      );
      logger.diagnostic.error(`BBP unregister failed: ${symbol}`, {
        symbol,
        error: String(err),
      });
    });
};

/**
 * Forward a raw `B` payload to every receiver that opts in via
 * `OnBookByPriceRaw(book)`. Does not mutate the payload.
 */
export const broadcastBookByPriceRawToReceivers = (md, book) => {
  if (!md.receivers || md.receivers.length === 0) return;
  const snapshot = md.receivers.slice(0);
  snapshot.forEach((receiver) => {
    if (receiver && typeof receiver.OnBookByPriceRaw === "function") {
      receiver.OnBookByPriceRaw(book);
    }
  });
};

/**
 * On reconnect, re-issue Register("bbp", symbol) once per active symbol.
 * Subscriber-level state lives in the manager; only the upstream
 * registration is repeated here.
 */
export const reregisterAllBookByPriceSymbols = (md) => {
  const symbols = md.bookByPriceSubscriptionManager.getActiveSymbols();
  if (symbols.length === 0) return;

  logger.diagnostic.info(
    `Re-registering ${symbols.length} BBP symbols after reconnect`,
    {
      event: "bbp-reregister-all",
      count: symbols.length,
      symbols,
    },
  );

  symbols.forEach((symbol) => {
    md.sendBookByPriceRegister(symbol);
  });
};

/**
 * Reset the quote worker's daily values cache for a set of symbols.
 * Forces the worker to send D* fields (DH, DL, PC, etc.) in full on the
 * next tick, instead of delta-filtering them as unchanged.
 * @param {Iterable<string>} symbols - Canonical symbols to reset
 */
export const resetWorkerDailyCache = (md, symbols) => {
  if (!md.quoteWorker) return;
  for (const sym of symbols) {
    md.quoteWorker.postMessage({ type: "RESET_DAILY_CACHE", symbol: sym });
  }
};

/**
 * Check if a symbol has any active subscribers.
 */
export const hasQuoteSubscribers = (md, symbol) => {
  return md.subscriptionManager.hasSubscribers(symbol);
};

/**
 * Debug: Log all quote subscriptions to console.
 * Call from DevTools: marketdata.debugQuoteSubscriptions() or window.debugQuotes()
 */
export const debugQuoteSubscriptions = (md) => {
  const data = md.subscriptionManager.debug();

  if (data.length === 0) {
    console.log("[Marketdata] No quote subscriptions");
    return [];
  }

  console.log(`[Marketdata] Quote Subscriptions (${data.length} symbols):`);
  console.table(data);
  return data;
};
